import React, { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, CheckCircle2, Clock, Code, FileText } from 'lucide-react';
import { webhookService, WebhookLog } from '@/services/webhookService';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

interface WebhookLogDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  webhookId: string;
  logId: string;
  onClose?: () => void;
}

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return 'Invalid date';
  }
};

const WebhookLogDetailSheet: React.FC<WebhookLogDetailSheetProps> = ({
  open,
  onOpenChange,
  clinicId,
  webhookId,
  logId,
  onClose,
}) => {
  const [log, setLog] = useState<WebhookLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && logId) {
      fetchLogDetail();
    } else {
      setLog(null);
      setError(null);
    }
  }, [open, logId, clinicId, webhookId]);

  const fetchLogDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await webhookService.getWebhookLogDetail(clinicId, webhookId, logId);
      setLog(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load log details';
      console.error('Failed to fetch log detail:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-6">
        <SheetHeader>
          <SheetTitle>Webhook Log Details</SheetTitle>
          <SheetDescription>
            Detailed information about this webhook delivery attempt
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error</h3>
            <p className="text-sm text-slate-600">{error}</p>
          </div>
        ) : log ? (
          <ScrollArea className="h-[calc(100vh-120px)] pr-4">
            <div className="space-y-6 py-4">
              {/* Summary */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Summary
                  </h3>
                  <Badge
                    variant={log.success ? 'default' : 'destructive'}
                    className={
                      log.success
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-red-100 text-red-800 border-red-200'
                    }
                  >
                    {log.success ? (
                      <>
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Success
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Failed
                      </>
                    )}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Event Type</label>
                    <p className="text-sm text-slate-900 mt-1 font-mono">{log.event_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Response Code</label>
                    <p className="text-sm text-slate-900 mt-1">
                      {log.response_status_code || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Response Time</label>
                    <p className="text-sm text-slate-900 mt-1">
                      {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Attempt Number</label>
                    <p className="text-sm text-slate-900 mt-1">{log.attempt_number}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Event ID</label>
                    <p className="text-sm text-slate-900 mt-1 font-mono break-all">
                      {log.event_id || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Timestamp</label>
                    <p className="text-sm text-slate-900 mt-1">{formatDate(log.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {log.error_message && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    Error Message
                  </h3>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-900">{log.error_message}</p>
                  </div>
                </div>
              )}

              {/* Retry Chain */}
              {log.retryLogs && log.retryLogs.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Retry Attempts
                  </h3>
                  <div className="space-y-2">
                    {log.retryLogs.map((retry) => (
                      <div
                        key={retry.id}
                        className="p-4 border border-slate-200 rounded-lg bg-slate-50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-900">
                            Attempt {retry.attempt_number}
                          </span>
                          <Badge
                            variant={retry.success ? 'default' : 'destructive'}
                            className={
                              retry.success
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : 'bg-red-100 text-red-800 border-red-200'
                            }
                          >
                            {retry.success ? 'Success' : 'Failed'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-slate-600">Response Code: </span>
                            <span className="text-slate-900">
                              {retry.response_status_code || '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-600">Response Time: </span>
                            <span className="text-slate-900">
                              {retry.response_time_ms ? `${retry.response_time_ms}ms` : '-'}
                            </span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-slate-600">Timestamp: </span>
                            <span className="text-slate-900">{formatDate(retry.created_at)}</span>
                          </div>
                          {retry.error_message && (
                            <div className="col-span-2">
                              <span className="text-slate-600">Error: </span>
                              <span className="text-red-900">{retry.error_message}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Request Payload */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  Request Payload
                </h3>
                <div className="p-4 bg-slate-900 rounded-lg overflow-x-auto">
                  <pre className="text-sm text-slate-100 font-mono">
                    {JSON.stringify(log.request_payload, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Request Headers */}
              {log.request_headers && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Code className="h-5 w-5" />
                    Request Headers
                  </h3>
                  <div className="p-4 bg-slate-900 rounded-lg overflow-x-auto">
                    <pre className="text-sm text-slate-100 font-mono">
                      {JSON.stringify(log.request_headers, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Request URL */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-slate-900">Request URL</h3>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-900 font-mono break-all">
                    {log.request_url}
                  </p>
                </div>
              </div>
            </div>
          </ScrollArea>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export default WebhookLogDetailSheet;

